import { Component, lazy, type ReactNode, Suspense } from "react"
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom"
import { Layout } from "./components/Layout"
import "./index.css"

class ErrorBoundary extends Component<
	{ children: ReactNode },
	{ error: Error | null }
> {
	override state = { error: null as Error | null }

	static getDerivedStateFromError(error: Error) {
		return { error }
	}

	override render() {
		if (this.state.error) {
			return (
				<div className="flex items-center justify-center h-64 text-red-400">
					<div className="text-center space-y-2">
						<p className="text-lg font-semibold">Something went wrong</p>
						<p className="text-sm text-neutral-500">
							{import.meta.env?.["DEV"]
								? this.state.error.message
								: "Please try again or refresh the page."}
						</p>
						<button
							className="px-4 py-1.5 rounded-lg bg-neutral-800 text-neutral-300 text-sm hover:bg-neutral-700"
							onClick={() => this.setState({ error: null })}
							type="button"
						>
							Try again
						</button>
					</div>
				</div>
			)
		}
		return this.props.children
	}
}

const Home = lazy(() => import("./pages/Home"))
const ColorSpaces = lazy(() => import("./pages/ColorSpaces"))
const PaletteGenerator = lazy(() => import("./pages/PaletteGenerator"))
const HarmonyWheel = lazy(() => import("./pages/HarmonyWheel"))
const Accessibility = lazy(() => import("./pages/Accessibility"))
const Adjustments = lazy(() => import("./pages/Adjustments"))
const BlendModes = lazy(() => import("./pages/BlendModes"))
const Interpolation = lazy(() => import("./pages/Interpolation"))
const ColorBlindness = lazy(() => import("./pages/ColorBlindness"))
const DataViz = lazy(() => import("./pages/DataViz"))
const Standards = lazy(() => import("./pages/Standards"))
const Convert = lazy(() => import("./pages/Convert"))
const ColorDiff = lazy(() => import("./pages/ColorDiff"))
const AnalogEffects = lazy(() => import("./pages/AnalogEffects"))
const ImageExtraction = lazy(() => import("./pages/ImageExtraction"))
const AlphaMixing = lazy(() => import("./pages/AlphaMixing"))
const CssPlayground = lazy(() => import("./pages/CssPlayground"))
const GamutMapping = lazy(() => import("./pages/GamutMapping"))
const ColorCorrection = lazy(() => import("./pages/ColorCorrection"))
const Help = lazy(() => import("./pages/Help"))
const NotFound = lazy(() => import("./pages/NotFound"))

function Loading() {
	return (
		<div className="flex items-center justify-center h-64 text-gray-500">
			Loading...
		</div>
	)
}

function AppContent() {
	const { pathname } = useLocation()
	return (
		<Layout>
			<ErrorBoundary key={pathname}>
				<Suspense fallback={<Loading />}>
					<Routes>
						<Route element={<Home />} path="/" />
						<Route element={<ColorSpaces />} path="/color-spaces" />
						<Route element={<PaletteGenerator />} path="/palettes" />
						<Route element={<HarmonyWheel />} path="/harmony" />
						<Route element={<Accessibility />} path="/accessibility" />
						<Route element={<Adjustments />} path="/adjustments" />
						<Route element={<BlendModes />} path="/blend-modes" />
						<Route element={<Interpolation />} path="/interpolation" />
						<Route element={<ColorBlindness />} path="/color-blindness" />
						<Route element={<DataViz />} path="/data-viz" />
						<Route element={<Standards />} path="/standards" />
						<Route element={<Convert />} path="/convert" />
						<Route element={<ColorDiff />} path="/color-diff" />
						<Route element={<AnalogEffects />} path="/analog-effects" />
						<Route element={<ImageExtraction />} path="/image-extraction" />
						<Route element={<AlphaMixing />} path="/alpha-mixing" />
						<Route element={<CssPlayground />} path="/css-playground" />
						<Route element={<GamutMapping />} path="/gamut-mapping" />
						<Route element={<ColorCorrection />} path="/color-correction" />
						<Route element={<Help />} path="/help" />
						<Route element={<NotFound />} path="*" />
					</Routes>
				</Suspense>
			</ErrorBoundary>
		</Layout>
	)
}

export function App() {
	return (
		<BrowserRouter>
			<AppContent />
		</BrowserRouter>
	)
}

export default App
